
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/utils/invokeEdgeFunction';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Plus, Trash2, Upload } from 'lucide-react';
import {
  CANDIDATE_STATUS,
  BRANCH_TYPES,
  QUALIFICATION_TYPES,
  EVALUATION_ROUNDS,
  DEFAULT_SKILLS,
  DEFAULT_TECHNOLOGIES,
  DEFAULT_HOBBIES,
} from '@/types/enums';
import { formatCandidateStatus, formatBranchType, formatQualificationType, getEvaluationRoundLabel, formatJobType } from '@/utils/formatters';
import { getCandidateStatusHint } from '@/utils/candidateStatus';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import type { Tables } from '@/integrations/supabase/types';
import { useAuthUsers } from '@/hooks/useAuthUsers';

type Candidate = Tables<'candidates'>;

interface CandidateFormProps {
  candidate?: Candidate;
  initialData?: Partial<CandidateFormData>;
  onSubmit: (data: CandidateFormData) => void;
  isSubmitting: boolean;
}

export interface CandidateFormData {
  full_name: string;
  email: string;
  phone?: string;
  branch?: string;
  qualification?: string;
  cgpa?: number;
  college_university?: string;
  latest_semester_score?: number;
  github_url?: string;
  linkedin_url?: string;
  permanent_address?: string;
  permanent_city?: string;
  current_address?: string;
  current_city?: string;
  position_applied?: string;
  hobbies_interests: string[];
  key_skills: string[];
  technologies: string[];
  status: string;
  current_round?: string;
  college_id?: string;
  job_position_id?: string;
  projects_details: string[];
  project_links: string[];
  certifications_awards: string[];
  event_participations: string[];
  internships_experience: string[];
  resume_url?: string;
  resume_markdown?: string;
  interviewer?: string[];
  joining_date?: string;
  created_at?: string;
  parsed_resume_data?: any;
}

export function CandidateForm({ candidate, initialData, onSubmit, isSubmitting }: CandidateFormProps) {
  const { toast } = useToast();
  const [isUploadingResume, setIsUploadingResume] = useState(false);

  const normalizeInterviewerValue = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [value];
    }

    return [];
  };

  // Ensure array fields are always arrays, never undefined
  const safeArrayValue = (value: any): string[] => {
    if (Array.isArray(value)) return value.filter(v => v && v.trim())
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map(item => typeof item === 'string' ? item : JSON.stringify(item)).filter(v => v.trim())
        }
      } catch {
        return [value.trim()]
      }
    }
    return []
  }

  const { control, register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CandidateFormData>({
    defaultValues: {
      full_name: initialData?.full_name ?? candidate?.full_name ?? '',
      email: initialData?.email ?? candidate?.email ?? '',
      phone: initialData?.phone ?? candidate?.phone ?? '',
      branch: initialData?.branch ?? candidate?.branch ?? '',
      qualification: initialData?.qualification ?? candidate?.qualification ?? '',
      cgpa: initialData?.cgpa ?? candidate?.cgpa ?? undefined,
      college_university: initialData?.college_university ?? candidate?.college_university ?? '',
      latest_semester_score: initialData?.latest_semester_score ?? candidate?.latest_semester_score ?? undefined,
      github_url: initialData?.github_url ?? candidate?.github_url ?? '',
      linkedin_url: initialData?.linkedin_url ?? candidate?.linkedin_url ?? '',
      permanent_address: initialData?.permanent_address ?? candidate?.permanent_address ?? '',
      permanent_city: initialData?.permanent_city ?? candidate?.permanent_city ?? '',
      current_address: initialData?.current_address ?? candidate?.current_address ?? '',
      current_city: initialData?.current_city ?? candidate?.current_city ?? '',
      position_applied: initialData?.position_applied ?? candidate?.position_applied ?? '',
      hobbies_interests: safeArrayValue(initialData?.hobbies_interests ?? candidate?.hobbies_interests),
      key_skills: safeArrayValue(initialData?.key_skills ?? candidate?.key_skills),
      technologies: safeArrayValue(initialData?.technologies ?? candidate?.technologies),
      status:
        initialData?.status ??
        candidate?.status ??
        CANDIDATE_STATUS.CV_SUBMITTED,
      current_round:
        initialData?.current_round ??
        candidate?.current_round ??
        EVALUATION_ROUNDS.CV_PROFILE,
      college_id: initialData?.college_id ?? candidate?.college_id ?? '',
      job_position_id: initialData?.job_position_id ?? candidate?.job_position_id ?? '',
      projects_details: safeArrayValue(initialData?.projects_details ?? candidate?.projects_details),
      project_links: safeArrayValue(initialData?.project_links ?? candidate?.project_links),
      certifications_awards: safeArrayValue(initialData?.certifications_awards ?? candidate?.certifications_awards),
      event_participations: safeArrayValue(initialData?.event_participations ?? candidate?.event_participations),
      internships_experience: safeArrayValue(initialData?.internships_experience ?? candidate?.internships_experience),
      resume_url: initialData?.resume_url ?? candidate?.resume_url ?? '',
      resume_markdown: initialData?.resume_markdown ?? candidate?.resume_markdown ?? '',
      interviewer: normalizeInterviewerValue(initialData?.interviewer ?? (candidate as any)?.interviewer),
      joining_date: initialData?.joining_date ?? candidate?.joining_date ?? '',
      created_at: initialData?.created_at
        ?? (candidate?.created_at ? candidate.created_at.slice(0, 16) : ''),
      parsed_resume_data: initialData?.parsed_resume_data ?? candidate?.parsed_resume_data ?? null,
    },
  });

  // Field arrays for dynamic text entries
  const { fields: projectFields, append: appendProject, remove: removeProject } = useFieldArray({
    control,
    name: 'projects_details'
  });

  const { fields: linkFields, append: appendLink, remove: removeLink } = useFieldArray({
    control,
    name: 'project_links'
  });

  const { fields: certificationFields, append: appendCertification, remove: removeCertification } = useFieldArray({
    control,
    name: 'certifications_awards'
  });

  const { fields: eventFields, append: appendEvent, remove: removeEvent } = useFieldArray({
    control,
    name: 'event_participations'
  });

  const { fields: experienceFields, append: appendExperience, remove: removeExperience } = useFieldArray({
    control,
    name: 'internships_experience'
  });

  // Fetch colleges for dropdown
  const { data: colleges } = useQuery({
    queryKey: ['colleges'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('colleges')
        .select('id, name, college_code')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch job positions for dropdown
  const { data: jobPositions } = useQuery({
    queryKey: ['job_positions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_positions')
        .select('id, role_name, job_type, interviewer')
        .eq('is_active', true)
        .order('role_name');
      if (error) throw error;
      return data;
    },
  });

  const { data: authUsers } = useAuthUsers();

  const handleResumeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate PDF file type
    if (file.type !== 'application/pdf') {
      toast({
        title: "Invalid File Type",
        description: "Please upload a PDF file only.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingResume(true);
    try {
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);

      // Upload to Cloudflare R2 (S3 compatible) via edge function
      const uploadResult = await invokeEdgeFunction<{ url: string }>('upload-resume', {
        body: formData
      });

      // Parse the uploaded PDF to markdown
      const parseResult = await invokeEdgeFunction<{ success: boolean; data: any; markdown: string | null }>('parse-resume-gemini', {
        body: { fileUrl: uploadResult.url }
      });

      // Update form values
      setValue('resume_url', uploadResult.url);
      setValue('resume_markdown', parseResult.markdown);
      setValue('parsed_resume_data', parseResult.data);

      toast({
        title: "Resume Uploaded",
        description: "Resume has been uploaded and processed successfully.",
      });
    } catch (error) {
      console.error('Resume upload error:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload and process resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingResume(false);
    }
  };

  const skillOptions = DEFAULT_SKILLS.map(skill => ({ label: skill, value: skill }));
  const technologyOptions = DEFAULT_TECHNOLOGIES.map(tech => ({ label: tech, value: tech }));
  const hobbyOptions = DEFAULT_HOBBIES.map(hobby => ({ label: hobby, value: hobby }));

  // URL validation regex
  const urlPattern = /^https?:\/\/.+/;

  // Phone validation regex (international format, allows spaces/dashes/parentheses)
  const phonePattern = /^\+?[\d\s\-().]{7,20}$/;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            {...register('full_name', { 
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' }
            })}
          />
          {errors.full_name && <p className="text-sm text-destructive mt-1">{errors.full_name.message}</p>}
        </div>

        <div>
          <Label htmlFor="interviewer">Interviewers</Label>
          <Controller
            name="interviewer"
            control={control}
            render={({ field }) => (
              <MultiSelect
                options={authUsers?.users.map((u) => ({ label: u.email || u.id, value: u.id })) ?? []}
                value={normalizeInterviewerValue(field.value)}
                onChange={field.onChange}
                placeholder="Select interviewers"
              />
            )}
          />
        </div>

        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
          />
          {errors.email && <p className="text-sm text-destructive mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            {...register('phone', {
              pattern: {
                value: phonePattern,
                message: 'Invalid phone number format'
              }
            })}
            placeholder="+1234567890"
          />
          {errors.phone && <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>}
        </div>

        <div>
          <Label htmlFor="college_id">College</Label>
          <Controller
            name="college_id"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select college" />
                </SelectTrigger>
                <SelectContent>
                  {colleges?.map((college) => (
                    <SelectItem key={college.id} value={college.id}>
                      {college.name} ({college.college_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div>
          <Label htmlFor="job_position_id">Position Applied</Label>
          <Controller
            name="job_position_id"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {jobPositions?.map((position) => (
                    <SelectItem key={position.id} value={position.id}>
                      {position.role_name} ({formatJobType(position.job_type)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div>
          <Label htmlFor="joining_date">Joining Date</Label>
          <Input id="joining_date" type="date" {...register('joining_date')} />
        </div>

        <div>
          <Label htmlFor="created_at">Registered At</Label>
          <Input id="created_at" type="datetime-local" {...register('created_at')} />
          <p className="text-xs text-muted-foreground mt-1">
            Change to correct the candidate's registration year
          </p>
        </div>

        <div>
          <Label htmlFor="qualification">Qualification</Label>
          <Controller
            name="qualification"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select qualification" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(QUALIFICATION_TYPES).map((qualification) => (
                    <SelectItem key={qualification} value={qualification}>
                      {formatQualificationType(qualification)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div>
          <Label htmlFor="branch">Branch</Label>
          <Controller
            name="branch"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(BRANCH_TYPES).map((branch) => (
                    <SelectItem key={branch} value={branch}>
                      {formatBranchType(branch)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div>
          <Label htmlFor="cgpa">CGPA</Label>
          <Input
            id="cgpa"
            type="number"
            step="0.01"
            min="0"
            max="10"
            {...register('cgpa', { 
              valueAsNumber: true,
              min: { value: 0, message: 'CGPA must be at least 0' },
              max: { value: 10, message: 'CGPA cannot exceed 10' }
            })}
          />
          {errors.cgpa && <p className="text-sm text-destructive mt-1">{errors.cgpa.message}</p>}
        </div>

        <div>
          <Label htmlFor="latest_semester_score">Latest Semester Score</Label>
          <Input
            id="latest_semester_score"
            type="number"
            step="0.01"
            {...register('latest_semester_score', { valueAsNumber: true })}
          />
        </div>

        <div>
          <Label htmlFor="github_url">GitHub URL</Label>
          <Input
            id="github_url"
            type="url"
            {...register('github_url', {
              pattern: {
                value: urlPattern,
                message: 'Invalid URL format'
              }
            })}
            placeholder="https://github.com/username"
          />
          {errors.github_url && <p className="text-sm text-destructive mt-1">{errors.github_url.message}</p>}
        </div>

        <div>
          <Label htmlFor="linkedin_url">LinkedIn URL</Label>
          <Input
            id="linkedin_url"
            type="url"
            {...register('linkedin_url', {
              pattern: {
                value: urlPattern,
                message: 'Invalid URL format'
              }
            })}
            placeholder="https://linkedin.com/in/username"
          />
          {errors.linkedin_url && <p className="text-sm text-destructive mt-1">{errors.linkedin_url.message}</p>}
        </div>

        <div>
          <Label htmlFor="status">Status</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(CANDIDATE_STATUS).map((status) => (
                      <SelectItem key={status} value={status}>
                        {formatCandidateStatus(status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {getCandidateStatusHint(watch('current_round'))}
            </p>
          </div>

        <div>
          <Label htmlFor="current_round">Current Round</Label>
          <Controller
            name="current_round"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select current round" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(EVALUATION_ROUNDS).map((round) => (
                    <SelectItem key={round} value={round}>
                      {getEvaluationRoundLabel(round)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="college_university">College/University</Label>
        <Input
          id="college_university"
          {...register('college_university')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="permanent_address">Permanent Address</Label>
          <Textarea
            id="permanent_address"
            {...register('permanent_address')}
            rows={3}
          />
        </div>

        <div>
          <Label htmlFor="permanent_city">Permanent City</Label>
          <Input
            id="permanent_city"
            {...register('permanent_city')}
            placeholder="Enter your permanent city"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="current_address">Current Address</Label>
          <Textarea
            id="current_address"
            {...register('current_address')}
            rows={3}
          />
        </div>

        <div>
          <Label htmlFor="current_city">Current City</Label>
          <Input
            id="current_city"
            {...register('current_city')}
            placeholder="Enter your current city"
          />
        </div>
      </div>

      {/* Resume Upload Section */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="resume_upload">Resume Upload (PDF only)</Label>
          <div className="flex items-center gap-4">
            <Input
              id="resume_upload"
              type="file"
              accept=".pdf"
              onChange={handleResumeUpload}
              disabled={isUploadingResume}
              className="flex-1"
            />
            {isUploadingResume && (
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4 animate-spin" />
                <span className="text-sm">Processing...</span>
              </div>
            )}
          </div>
          {watch('resume_url') && (
            <p className="text-sm text-green-600 mt-1">
              Resume uploaded successfully: <a href={watch('resume_url')} target="_blank" rel="noopener noreferrer" className="underline">View</a>
            </p>
          )}
        </div>
      </div>

      {/* Dynamic Text Fields */}
      <div className="space-y-6">
        {/* Projects Details */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Project Details</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => appendProject('')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Project
            </Button>
          </div>
          {projectFields.map((field, index) => (
            <div key={field.id} className="flex gap-2 mb-2">
              <Textarea
                {...register(`projects_details.${index}` as const)}
                placeholder="Describe your project, technologies used, your role, etc."
                rows={3}
              />
              {projectFields.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeProject(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {projectFields.length === 0 && (
            <p className="text-sm text-muted-foreground">No projects added yet. Click "Add Project" to get started.</p>
          )}
        </div>

        {/* Project Links */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Project Links</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => appendLink('')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Link
            </Button>
          </div>
          {linkFields.map((field, index) => (
            <div key={field.id} className="flex gap-2 mb-2">
              <Textarea
                {...register(`project_links.${index}` as const)}
                placeholder="Portfolio links, GitHub repositories, deployed applications, etc."
                rows={2}
              />
              {linkFields.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeLink(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {linkFields.length === 0 && (
            <p className="text-sm text-muted-foreground">No links added yet. Click "Add Link" to get started.</p>
          )}
        </div>

        {/* Certifications & Awards */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Certifications & Awards</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => appendCertification('')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Certification
            </Button>
          </div>
          {certificationFields.map((field, index) => (
            <div key={field.id} className="flex gap-2 mb-2">
              <Textarea
                {...register(`certifications_awards.${index}` as const)}
                placeholder="List your certifications, awards, achievements, etc."
                rows={2}
              />
              {certificationFields.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeCertification(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {certificationFields.length === 0 && (
            <p className="text-sm text-muted-foreground">No certifications added yet. Click "Add Certification" to get started.</p>
          )}
        </div>

        {/* Event Participations */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Event Participations</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => appendEvent('')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Event
            </Button>
          </div>
          {eventFields.map((field, index) => (
            <div key={field.id} className="flex gap-2 mb-2">
              <Textarea
                {...register(`event_participations.${index}` as const)}
                placeholder="Hackathons, competitions, conferences, workshops attended, etc."
                rows={2}
              />
              {eventFields.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeEvent(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {eventFields.length === 0 && (
            <p className="text-sm text-muted-foreground">No events added yet. Click "Add Event" to get started.</p>
          )}
        </div>

        {/* Internships & Experience */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Internships & Experience</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => appendExperience('')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Experience
            </Button>
          </div>
          {experienceFields.map((field, index) => (
            <div key={field.id} className="flex gap-2 mb-2">
              <Textarea
                {...register(`internships_experience.${index}` as const)}
                placeholder="Previous internships, work experience, roles, responsibilities, etc."
                rows={3}
              />
              {experienceFields.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeExperience(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {experienceFields.length === 0 && (
            <p className="text-sm text-muted-foreground">No experience added yet. Click "Add Experience" to get started.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Key Skills</Label>
          <Controller
            name="key_skills"
            control={control}
            render={({ field }) => (
              <MultiSelect
                options={skillOptions}
                value={safeArrayValue(field.value)}
                onChange={(value) => field.onChange(safeArrayValue(value))}
                placeholder="Select or add skills..."
                allowCustom={true}
              />
            )}
          />
        </div>

        <div>
          <Label>Technologies</Label>
          <Controller
            name="technologies"
            control={control}
            render={({ field }) => (
              <MultiSelect
                options={technologyOptions}
                value={safeArrayValue(field.value)}
                onChange={(value) => field.onChange(safeArrayValue(value))}
                placeholder="Select or add technologies..."
                allowCustom={true}
              />
            )}
          />
        </div>

        <div>
          <Label>Hobbies & Interests</Label>
          <Controller
            name="hobbies_interests"
            control={control}
            render={({ field }) => (
              <MultiSelect
                options={hobbyOptions}
                value={safeArrayValue(field.value)}
                onChange={(value) => field.onChange(safeArrayValue(value))}
                placeholder="Select or add hobbies..."
                allowCustom={true}
              />
            )}
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting || isUploadingResume} className="w-full">
        {isSubmitting ? 'Saving...' : candidate ? 'Update Candidate' : 'Create Candidate'}
      </Button>
    </form>
  );
}
